<script setup lang="ts">
import { ref, watch, watchEffect } from "vue";
import { getCSSVar, ColorPattern } from "@zyzgroup/core-web";
import { context, getColors } from "../../context/context";
import Pop from "../pop/Pop.vue";
import Button, { ButtonModePattern } from "../form/Button.vue";
import IconClose from "../icon/IconClose.vue";
import IconWarn from "../icon/IconWarn.vue";
import IconEllipse from "../icon/IconEllipse.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {
    Pop,
    Button,
    IconClose,
    IconWarn,
    IconEllipse
  },
  inheritAttrs: true
});

export type DialogModeType = "normal" | "mini" | "icon-btns" | "btns";

const props = withDefaults(
  defineProps<{
    mode?: DialogModeType;
    colorPattern?: ColorPattern;
    title?: string;
    content?: string;
    showClose?: boolean;
    cancelText?: string;
    confirmText?: string;
    closeWhenClickOverlay?: boolean;
    dur?: number;
    minWidth?: string;
    maxWidth?: string;
    borderRadius?: string; // border-radius:x/y
  }>(),
  {
    mode: "normal",
    colorPattern: context.store.getState().color
      .colorPattern as unknown as ColorPattern,
    title: "",
    content: "",
    showClose: false,
    cancelText: "",
    confirmText: "",
    closeWhenClickOverlay: false,
    dur: 200,
    minWidth: "280px",
    maxWidth: "320px",
    borderRadius: getCSSVar("--yz-dialog-border-radius", "12px")
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "cancel"): void;
  (event: "confirm"): void;
}>();

const popRef = ref();

const primaryPattern = ref();
const gradientPattern = ref();
const foregroundReverse = ref();
const fontReverse = ref();

watchEffect(() => {
  const colors = getColors(props.colorPattern);
  primaryPattern.value = colors.primaryPattern;
  gradientPattern.value = colors.gradientPattern;
  foregroundReverse.value = colors.foregroundReverse;
  fontReverse.value = colors.fontReverse;
});

const cancelButtonMode = ref<ButtonModePattern>("transparent");
const confirmButtonMode = ref<ButtonModePattern>("transparent");
const showTopIcon = ref(false);
const isBtnsVertical = ref(false);
const btnCapsule = ref(false);

watch(
  () => props.mode,
  () => {
    switch (props.mode) {
      case "normal":
        cancelButtonMode.value = "normal-reverse";
        confirmButtonMode.value = "primary";
        showTopIcon.value = false;
        isBtnsVertical.value = false;
        btnCapsule.value = false;
        break;
      case "mini":
        cancelButtonMode.value = "normal-transparent";
        confirmButtonMode.value = "transparent";
        showTopIcon.value = false;
        isBtnsVertical.value = false;
        btnCapsule.value = false;
        break;
      case "icon-btns":
        cancelButtonMode.value = "outline";
        confirmButtonMode.value = "gradient";
        showTopIcon.value = true;
        isBtnsVertical.value = true;
        btnCapsule.value = true;
        break;
      case "btns":
        cancelButtonMode.value = "outline";
        confirmButtonMode.value = "gradient";
        showTopIcon.value = false;
        isBtnsVertical.value = true;
        btnCapsule.value = true;
        break;
    }
  },
  { immediate: true }
);

const popOpen = () => {
  emits("open");
};
const popClose = () => {
  emits("close");
  switch (action) {
    case "close":
      emits("cancel");
      break;
    case "confirm":
      emits("confirm");
      break;
  }
};

let action: "open" | "close" | "confirm";

function open() {
  action = "open";
  popRef.value?.open();
}

function close() {
  action = "close";
  popRef.value?.close();
}

function confirm() {
  action = "confirm";
  popRef.value?.close();
}

defineExpose({
  open,
  close
});
</script>

<template>
  <Pop
    ref="popRef"
    directionFrom="center"
    :closeWhenClickOverlay="props.closeWhenClickOverlay"
    :dur="props.dur"
    @open="popOpen"
    @close="popClose"
  >
    <div class="dialog-parent">
      <div
        class="dialog"
        :style="{
          minWidth: props.minWidth,
          maxWidth: props.maxWidth,
          color: fontReverse
        }"
      >
        <!-- 头部提示图片 -->
        <div
          v-if="showTopIcon"
          class="top-icon"
        >
          <div class="top-icon-btns">
            <IconWarn
              :isGradient="true"
              :color="primaryPattern"
              :colorEnd="gradientPattern"
            ></IconWarn>
          </div>
          <div class="top-icon-btns-shadow"><IconEllipse></IconEllipse></div>
        </div>
        <!-- 主体 -->
        <div
          class="dialog-inner"
          :style="{
            paddingTop: showTopIcon ? '24px' : '0px',
            borderRadius: props.borderRadius,
            backgroundColor: foregroundReverse
          }"
        >
          <!-- 标题 -->
          <div
            v-if="title || showClose"
            class="title"
          >
            <div
              v-if="title"
              class="title-inner"
            >
              {{ title }}
            </div>
            <div
              v-if="showClose"
              class="close"
              @click="close"
            >
              <div><IconClose></IconClose></div>
            </div>
          </div>
          <!-- 内容 -->
          <div class="custom-content">
            <slot>
              <div class="custom-content-default">{{ content }}</div>
            </slot>
          </div>
          <!-- 按钮 -->
          <div
            class="btns"
            :class="[props.mode, isBtnsVertical ? 'vertical' : '']"
            v-if="cancelText || confirmText"
          >
            <Button
              v-if="cancelText"
              class="btn cancel"
              :mode="cancelButtonMode"
              :colorPattern="props.colorPattern"
              :capsule="btnCapsule"
              borderRadius="0"
              :fontWeight="props.mode == 'normal' ? 'normal' : 'bold'"
              @throttledClick="close"
            >
              {{ cancelText }}
            </Button>
            <Button
              v-if="confirmText"
              class="btn confirm"
              :mode="confirmButtonMode"
              :colorPattern="props.colorPattern"
              :capsule="btnCapsule"
              borderRadius="0"
              fontWeight="bold"
              @click="confirm"
            >
              {{ confirmText }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Pop>
</template>

<style scoped>
.dialog-parent {
  display: flex;
  justify-content: center;
  align-items: center;
}
.dialog {
  position: relative;
}
.top-icon {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.top-icon-btns {
  width: 60px;
  height: 60px;
}
.top-icon-btns-shadow {
  width: 40px;
  height: 4px;
  margin-top: 4px;
}
.dialog-inner {
  overflow: hidden;
}
.title {
  position: relative;
  margin-top: 20px;
  font-size: 16px;
  margin-bottom: -5px;
}
.title-inner {
  padding: 0 42px;
  text-align: center;
  font-weight: bold;
}
.close {
  position: absolute;
  top: 0;
  right: 0;
  width: 32px;
  height: 100%;
  text-align: center;
  display: flex;
  justify-content: left;
  align-items: center;
  cursor: pointer;
}
.close > div {
  width: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.custom-content {
  font-size: 14px;
  word-break: break-all;
  text-align: center;
  max-height: 6rem;
  max-height: 60vh;
  overflow-y: scroll;
  color: var(--yz-font-secondary-reverse, #666666);
  padding: 20px;
}
.custom-content-default {
  padding: 0;
}
.btns {
  display: flex;
  justify-content: center;
}
.btns.vertical {
  flex-direction: column;
  padding-bottom: 20px;
}
.btns.vertical > .btn {
  margin-left: 20px;
  margin-right: 20px;
}
.btns.vertical > .btn.cancel {
  order: 2;
  margin-top: 10px;
}
.btns.vertical > .btn.confirm {
  order: 1;
}
.btns.mini {
  border-top: 1px solid var(--yz-foreground-border-reverse, #e6e6e6);
}
.btns.mini > .btn + .btn {
  border-left: 1px solid var(--yz-foreground-border-reverse, #e6e6e6) !important;
}
.btn {
  flex: 1 1 auto;
  border-radius: 0;
}
</style>
